import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Search, GitFork, Star, Copy, Check, ChevronRight, ChevronDown,
  ExternalLink, ArrowRight, ArrowUpRight, Menu, X, Zap, Code2, Server,
  AlertTriangle, Globe, ShieldCheck, Clock, BookOpen,
  Sparkles, Terminal, Languages, ArrowLeft, Sun, Moon,
  CreditCard, Wallet, Activity, Lock, FlaskConical,
  ChevronUp, FileText, Boxes, ListChecks, Hash, Camera,
} from 'lucide-react';
import { copyToClipboard, cn } from '../lib/utils';

/* ===========================================================================
   PublicDocsPage — Firecrawl-style documentation page.

   Layout:
     ┌──────────────────────────────────────────────────────────────────┐
     │ [logo] Datames  v1          🔍 Search… ⌘K   GH★ Status  Sign│  ← top nav
     ├──────────────────────────────────────────────────────────────────┤
     │ Documentation  Integrations  API Reference  Build with AI        │  ← sub-nav
     ├──────────┬───────────────────────────────────────────┬──────────┤
     │          │                                           │          │
     │  side    │           main content                    │  on this │
     │  nav     │                                           │  page    │
     │          │                                           │          │
     └──────────┴───────────────────────────────────────────┴──────────┘

   Public — accessible without login. The page uses its own minimal layout
   and doesn't pull from any auth-protected state. Authenticated users
   visiting /docs see the same content.
   =========================================================================== */

const NAV = [
  {
    label: 'External',
    items: [
      { href: '/playground', label: 'Playground', icon: FlaskConical, target: '_self' },
      { href: '/captcha-playground', label: 'Captcha playground', icon: ShieldCheck, target: '_self' },
    ],
  },
  {
    label: 'Get started',
    items: [
      { slug: 'introduction', label: 'Introduction', icon: BookOpen },
      { slug: 'quickstart', label: 'Quickstart', icon: Zap },
      { slug: 'engines', label: 'Engines', icon: Boxes },
      { slug: 'cloudflare', label: 'Cloudflare handling', icon: ShieldCheck },
    ],
  },
  {
    label: "What's new",
    items: [
      { slug: 'changelog', label: 'Recent updates', icon: Sparkles },
    ],
  },
  {
    label: 'Core endpoints',
    items: [
      { slug: 'fetch', label: 'POST /v1/fetch', icon: Code2 },
      { slug: 'captcha', label: 'POST /captcha', icon: ShieldCheck },
      { slug: 'keys', label: 'API keys', icon: Terminal },
      { slug: 'billing', label: 'Billing', icon: CreditCard },
      { slug: 'analytics', label: 'Analytics', icon: Activity },
      { slug: 'errors', label: 'Error codes', icon: AlertTriangle },
    ],
  },
  {
    label: 'Plans & billing',
    items: [
      { slug: 'plans', label: 'Plans', icon: CreditCard },
      { slug: 'credits', label: 'Credits', icon: Wallet },
    ],
  },
];

const SECTIONS = {
  introduction: {
    title: 'Introduction',
    eyebrow: 'Get Started',
    render: IntroductionSection,
    onPage: ['Introduction', 'What can Datames do?', 'Why Datames?'],
  },
  quickstart: {
    title: 'Quickstart',
    eyebrow: 'Get Started',
    render: QuickstartSection,
    onPage: ['Get an API key', 'Make your first request', 'Try the Playground'],
  },
  engines: {
    title: 'Engines',
    eyebrow: 'Get Started',
    render: EnginesSection,
    onPage: ['Dynamic (default)', 'Classic', 'Stealth (Datames)', 'Choosing an engine'],
  },
  cloudflare: {
    title: 'Cloudflare handling',
    eyebrow: 'Get Started',
    render: CloudflareSection,
    onPage: ['How it works', 'Caching', 'When it escalates'],
  },
  changelog: {
    title: 'Recent updates',
    eyebrow: "What's new",
    render: ChangelogSection,
    onPage: ['Latest changes', 'In progress'],
  },
  fetch: {
    title: 'POST /v1/fetch',
    eyebrow: 'Core endpoints',
    render: FetchSection,
    onPage: ['Endpoint', 'Request body', 'Response shape', 'Examples'],
  },
  captcha: {
    title: 'POST /captcha',
    eyebrow: 'Core endpoints',
    render: CaptchaSection,
    onPage: ['Overview', 'recaptcha_v2_proxyless', 'recaptcha_v2_proxy', 'recaptcha_v3', 'hcaptcha', 'turnstile', 'geetest', 'imageToText', 'Errors', 'Examples'],
  },
  keys: {
    title: 'API keys',
    eyebrow: 'Core endpoints',
    render: KeysSection,
    onPage: ['List keys', 'Create a key', 'Use a key', 'Revoke a key'],
  },
  billing: {
    title: 'Billing',
    eyebrow: 'Core endpoints',
    render: BillingSection,
    onPage: ['Plans', 'Subscription', 'Top-ups', 'Usage analytics'],
  },
  analytics: {
    title: 'Analytics',
    eyebrow: 'Core endpoints',
    render: AnalyticsSection,
    onPage: ['Summary', 'Logs', 'Daily usage', 'Per-engine cost'],
  },
  errors: {
    title: 'Error codes',
    eyebrow: 'Core endpoints',
    render: ErrorsSection,
    onPage: ['HTTP status codes', 'Common error responses', 'Retries'],
  },
  plans: {
    title: 'Plans',
    eyebrow: 'Plans & billing',
    render: PlansSection,
    onPage: ['Free', 'Starter', 'Pro', 'Enterprise', 'Choosing a plan'],
  },
  credits: {
    title: 'Credits',
    eyebrow: 'Plans & billing',
    render: CreditsSection,
    onPage: ['How credits work', 'What costs more', 'Auto top-up', 'Refunds'],
  },
};

export default function PublicDocsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSlug, setActiveSlug] = useState('introduction');
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Slug = the part after /docs/ (defaults to "introduction")
  const slug = useMemo(() => {
    const parts = location.pathname.replace(/\/$/, '').split('/');
    const last = parts[parts.length - 1];
    if (last === 'docs' || last === '') return 'introduction';
    return last;
  }, [location.pathname]);

  useEffect(() => {
    setActiveSlug(SECTIONS[slug] ? slug : 'introduction');
    setMobileNavOpen(false);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  // Update URL when clicking sidebar links
  function goTo(s) {
    navigate(`/docs/${s}`);
  }

  // Cmd/Ctrl+K to open search
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const section = SECTIONS[activeSlug] || SECTIONS.introduction;
  const SectionComponent = section.render;

  return (
    <div className="min-h-screen bg-bg text-fg">
      {/* ===================== TOP NAV ===================== */}
      <TopNav onSearch={() => setSearchOpen(true)} />

      {/* ===================== SUB-NAV (Documentation tabs) ===================== */}
      <SubNav />

      {/* ===================== MAIN LAYOUT ===================== */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr_220px] gap-6 lg:gap-10">
          {/* Left sidebar */}
          <aside className="hidden lg:block py-8">
            <Sidebar activeSlug={activeSlug} onNavigate={goTo} />
          </aside>

          {/* Main content */}
          <main className="py-8 min-w-0">
            {/* Mobile sidebar trigger */}
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className="lg:hidden inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-md border border-border bg-surface text-xs text-fg-muted hover:text-fg"
            >
              <Menu className="h-3.5 w-3.5" />
              Documentation menu
            </button>

            <div className="text-2xs uppercase tracking-wider text-fg-subtle font-medium">
              {section.eyebrow}
            </div>
            <div className="mt-1 flex items-start justify-between gap-3">
              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-fg">
                {section.title}
              </h1>
              <button
                type="button"
                onClick={async () => {
                  const path = `${location.pathname}`;
                  const url = `${window.location.origin}${path}`;
                  if (await copyToClipboard(url)) {
                    // tiny visual feedback is handled by the page header, no toast needed
                  }
                }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border bg-surface text-xs text-fg-muted hover:text-fg transition-colors shrink-0 mt-1.5"
                title="Copy page link"
              >
                <Copy className="h-3 w-3" />
                <span className="hidden sm:inline">Copy page</span>
              </button>
            </div>
            <hr className="my-5 border-border" />

            <div className="space-y-12">
              <SectionComponent />
            </div>

            {/* Footer nav: prev/next */}
            <FooterNav activeSlug={activeSlug} onNavigate={goTo} />
          </main>

          {/* Right sidebar: on this page */}
          <aside className="hidden lg:block py-8">
            <OnThisPage items={section.onPage} />
          </aside>
        </div>
      </div>

      {/* Mobile sidebar drawer */}
      {mobileNavOpen && (
        <MobileSidebar
          activeSlug={activeSlug}
          onNavigate={goTo}
          onClose={() => setMobileNavOpen(false)}
        />
      )}

      {/* Search modal */}
      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} onNavigate={goTo} />}
    </div>
  );
}

/* ===========================================================================
   TOP NAV
   =========================================================================== */
function TopNav({ onSearch }) {
  return (
    <header className="sticky top-0 z-30 border-b border-border bg-bg/95 backdrop-blur supports-[backdrop-filter]:bg-bg/75">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center gap-4">
        {/* Logo */}
        <Link to="/docs" className="flex items-center gap-2 shrink-0">
          <div className="h-7 w-7 rounded-md bg-gradient-to-br from-brand to-orange-400 flex items-center justify-center text-white">
            <FlaskConical className="h-4 w-4" />
          </div>
          <div className="hidden sm:block leading-tight">
            <div className="text-sm font-semibold text-fg">Datames</div>
          </div>
        </Link>

        {/* Version pill */}
        <span className="hidden md:inline-flex items-center px-2 py-0.5 rounded-full border border-border text-2xs text-fg-muted bg-surface">
          v1
        </span>

        <div className="flex-1" />

        {/* Search */}
        <button
          type="button"
          onClick={onSearch}
          className="hidden sm:inline-flex items-center gap-2 min-w-[200px] px-3 h-8 rounded-md bg-surface border border-border text-xs text-fg-muted hover:text-fg transition-colors"
        >
          <Search className="h-3.5 w-3.5" />
          <span className="flex-1 text-left">Search docs…</span>
          <kbd className="hidden md:inline-block px-1.5 py-0.5 rounded bg-bg border border-border font-mono text-2xs text-fg-subtle">⌘K</kbd>
        </button>

        {/* GitHub */}

        {/* Status */}
        <Link
          to="/status"
          className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md border border-border bg-surface text-xs text-fg-muted hover:text-fg transition-colors"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-success" />
          Status
        </Link>

        {/* Support */}
        <a
          href="mailto:support@datames.com"
          className="hidden sm:inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md border border-border bg-surface text-xs text-fg-muted hover:text-fg transition-colors"
        >
          Support
        </a>

        {/* Sign up CTA */}
        <Link
          to="/signup"
          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-brand text-brand-fg text-xs font-medium hover:opacity-90 transition-opacity"
        >
          Sign up
          <ArrowRight className="h-3 w-3" />
        </Link>

        {/* Theme toggle (visual only) */}
        <button
          type="button"
          className="hidden md:inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-surface text-fg-muted hover:text-fg transition-colors"
          aria-label="Toggle theme"
        >
          <Sun className="h-3.5 w-3.5" />
        </button>
      </div>
    </header>
  );
}

/* ===========================================================================
   SUB-NAV
   =========================================================================== */
function SubNav() {
  const location = useLocation();
  const tabs = [
    { label: 'Documentation', value: 'documentation' },
    { label: 'Reference', value: 'reference' },
  ];
  const activeTab = location.pathname.startsWith('/docs') ? 'documentation' : '';
  return (
    <nav className="border-b border-border bg-bg">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-6 h-11 overflow-x-auto">
          {tabs.map((t) => (
            <Link
              key={t.value}
              to={t.value === 'documentation' ? '/docs' : '/docs'}
              className={cn(
                'relative inline-flex items-center text-xs font-medium whitespace-nowrap transition-colors py-3',
                activeTab === t.value
                  ? 'text-fg'
                  : 'text-fg-muted hover:text-fg'
              )}
            >
              {t.label}
              {activeTab === t.value && (
                <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-fg rounded-t" />
              )}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
}

/* ===========================================================================
   LEFT SIDEBAR (sidebar nav with collapsible sections)
   =========================================================================== */
function Sidebar({ activeSlug, onNavigate }) {
  return (
    <nav className="space-y-6 sticky top-[80px]">
      {NAV.map((group) => (
        <div key={group.label}>
          <div className="text-2xs uppercase tracking-wider text-fg-subtle font-semibold px-2 mb-2">
            {group.label}
          </div>
          <ul className="space-y-0.5">
            {group.items.map((item) => (
              <SidebarItem
                key={item.label}
                item={item}
                active={item.slug === activeSlug}
                onNavigate={onNavigate}
              />
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function SidebarItem({ item, active, onNavigate }) {
  const Icon = item.icon;
  const baseClass = cn(
    'group flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors w-full text-left',
    active
      ? 'bg-surface text-fg font-medium'
      : 'text-fg-muted hover:text-fg hover:bg-surface/60'
  );

  if (item.comingSoon || item.href === '#') {
    return (
      <li>
        <span className={`${baseClass} opacity-50 cursor-not-allowed`}>
          <Icon className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate flex-1">{item.label}</span>
          <span className="text-2xs text-fg-subtle">soon</span>
        </span>
      </li>
    );
  }

  if (item.target === '_self') {
    return (
      <li>
        <Link to={item.href} className={baseClass}>
          <Icon className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate flex-1">{item.label}</span>
          <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-50" />
        </Link>
      </li>
    );
  }

  return (
    <li>
      <button type="button" onClick={() => onNavigate(item.slug)} className={baseClass}>
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate flex-1">{item.label}</span>
        {active && <span className="ml-auto h-1 w-1 rounded-full bg-brand" />}
      </button>
    </li>
  );
}

/* ===========================================================================
   MOBILE SIDEBAR DRAWER
   =========================================================================== */
function MobileSidebar({ activeSlug, onNavigate, onClose }) {
  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-bg border-r border-border overflow-y-auto p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-gradient-to-br from-brand to-orange-400 flex items-center justify-center text-white">
              <FlaskConical className="h-4 w-4" />
            </div>
            <span className="text-sm font-semibold">Datames</span>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-surface">
            <X className="h-4 w-4" />
          </button>
        </div>
        <Sidebar activeSlug={activeSlug} onNavigate={(s) => { onNavigate(s); onClose(); }} />
      </div>
    </div>
  );
}

/* ===========================================================================
   ON THIS PAGE (right sidebar)
   =========================================================================== */
function OnThisPage({ items }) {
  return (
    <nav className="sticky top-[80px] space-y-2">
      <div className="flex items-center gap-1.5 text-2xs uppercase tracking-wider text-fg-subtle font-semibold">
        <ListChecks className="h-3 w-3" />
        On this page
      </div>
      <ul className="space-y-1 border-l border-border">
        {items.map((it, i) => (
          <li key={i}>
            <a
              href={`#${it.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
              className="block -ml-px pl-3 py-1 text-xs text-fg-muted hover:text-fg border-l border-transparent hover:border-fg-muted transition-colors"
            >
              {it}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/* ===========================================================================
   CONTENT SECTIONS — each section is a small reusable block
   =========================================================================== */
function Anchor({ id, children }) {
  return (
    <h2 id={id} className="text-xl font-semibold text-fg mt-10 mb-3 scroll-mt-24 first:mt-0">
      {children}
    </h2>
  );
}

function Lead({ children }) {
  return <p className="text-base text-fg-muted leading-relaxed">{children}</p>;
}

function Callout({ icon: Icon, color = 'info', children, title }) {
  const palette = {
    info:    { bd: 'border-info/30', bg: 'bg-info/10', fg: 'text-info', ic: 'text-info' },
    warning: { bd: 'border-warning/30', bg: 'bg-warning/10', fg: 'text-warning', ic: 'text-warning' },
    success: { bd: 'border-success/30', bg: 'bg-success/10', fg: 'text-success', ic: 'text-success' },
  }[color];
  return (
    <div className={`my-5 p-4 rounded-lg border ${palette.bd} ${palette.bg}`}>
      <div className="flex items-start gap-3">
        {Icon && <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${palette.ic}`} />}
        <div className="text-sm text-fg-muted leading-relaxed">
          {title && <div className={`font-medium mb-0.5 ${palette.fg}`}>{title}</div>}
          <div>{children}</div>
        </div>
      </div>
    </div>
  );
}

function EndpointHeader({ method, path }) {
  const color = {
    GET:    'bg-success/15 text-success border-success/30',
    POST:   'bg-info/15 text-info border-info/30',
    PUT:    'bg-warning/15 text-warning border-warning/30',
    DELETE: 'bg-destructive/15 text-destructive border-destructive/30',
  }[method];
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-surface">
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-2xs font-mono font-semibold border ${color}`}>
        {method}
      </span>
      <code className="font-mono text-sm text-fg">{path}</code>
    </div>
  );
}

function CodeBlock({ code, language = 'bash', showLineNumbers = false, filename }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    if (await copyToClipboard(code)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }, [code]);

  return (
    <div className="my-3 rounded-lg overflow-hidden border border-border bg-[#0f1115] dark:bg-[#0a0c10]">
      {filename && (
        <div className="flex items-center justify-between px-4 py-1.5 bg-black/30 text-xs font-mono text-white/60">
          <span className="flex items-center gap-1.5">
            <FileText className="h-3 w-3" />
            {filename}
          </span>
          <span className="text-2xs uppercase opacity-60">{language}</span>
        </div>
      )}
      <div className="relative group">
        <pre className={`px-4 py-3 overflow-x-auto text-xs font-mono leading-relaxed text-white/90 ${showLineNumbers ? 'pl-12' : ''}`}>
          <code>{code}</code>
        </pre>
        <button
          type="button"
          onClick={handleCopy}
          className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 text-2xs font-medium rounded bg-white/10 text-white/70 hover:bg-white/20 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Copy code"
        >
          {copied ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
        </button>
      </div>
    </div>
  );
}

function ParamTable({ rows }) {
  return (
    <div className="my-4 rounded-lg border border-border overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-surface-2/40">
          <tr className="text-2xs font-semibold uppercase tracking-wider text-fg-muted">
            <th className="text-left px-3 py-2">Field</th>
            <th className="text-left px-3 py-2">Type</th>
            <th className="text-left px-3 py-2 w-1/2">Description</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border align-top">
              <td className="px-3 py-2 font-mono">
                {r.name}
                {r.required && <span className="ml-0.5 text-destructive">*</span>}
              </td>
              <td className="px-3 py-2 font-mono text-fg-muted whitespace-nowrap">{r.type}</td>
              <td className="px-3 py-2 text-fg-muted leading-relaxed">{r.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Cards({ items }) {
  return (
    <div className="my-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
      {items.map((it, i) => (
        <a
          key={i}
          href={it.href || '#'}
          className="group block p-4 rounded-xl border border-border bg-surface hover:border-fg-subtle hover:bg-surface-2/30 transition-all"
        >
          <div className="flex items-center gap-2 mb-1.5">
            {it.icon && <it.icon className="h-4 w-4 text-brand-strong" />}
            <div className="text-sm font-semibold text-fg">{it.title}</div>
            <ArrowUpRight className="h-3 w-3 ml-auto text-fg-subtle opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          {it.subtitle && <div className="text-2xs text-fg-muted mb-1.5">{it.subtitle}</div>}
          <div className="text-xs text-fg-muted leading-relaxed">{it.desc}</div>
        </a>
      ))}
    </div>
  );
}

function FooterNav({ activeSlug, onNavigate }) {
  const order = Object.keys(SECTIONS);
  const idx = order.indexOf(activeSlug);
  const prev = idx > 0 ? SECTIONS[order[idx - 1]] : null;
  const next = idx < order.length - 1 ? SECTIONS[order[idx + 1]] : null;
  return (
    <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-border pt-6">
      {prev ? (
        <button
          type="button"
          onClick={() => onNavigate(prev.slugForFooter || order[idx - 1])}
          className="group flex items-center gap-3 px-4 py-3 rounded-lg border border-border bg-surface hover:bg-surface-2/30 text-left transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5 text-fg-muted group-hover:text-fg" />
          <div>
            <div className="text-2xs uppercase tracking-wider text-fg-muted">Previous</div>
            <div className="text-sm font-medium text-fg">{prev.title}</div>
          </div>
        </button>
      ) : <div />}
      {next ? (
        <button
          type="button"
          onClick={() => onNavigate(order[idx + 1])}
          className="group flex items-center justify-end gap-3 px-4 py-3 rounded-lg border border-border bg-surface hover:bg-surface-2/30 text-right transition-colors"
        >
          <div>
            <div className="text-2xs uppercase tracking-wider text-fg-muted">Next</div>
            <div className="text-sm font-medium text-fg">{next.title}</div>
          </div>
          <ArrowRight className="h-3.5 w-3.5 text-fg-muted group-hover:text-fg" />
        </button>
      ) : <div />}
    </div>
  );
}

/* ===========================================================================
   SEARCH MODAL
   =========================================================================== */
function SearchModal({ onClose, onNavigate }) {
  const [q, setQ] = useState('');
  const results = useMemo(() => {
    if (!q.trim()) return [];
    const needle = q.toLowerCase();
    return Object.entries(SECTIONS)
      .filter(([slug, s]) =>
        s.title.toLowerCase().includes(needle) || (s.onPage || []).some((p) => p.toLowerCase().includes(needle))
      )
      .map(([slug, s]) => ({ slug, title: s.title, eyebrow: s.eyebrow }));
  }, [q]);

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative max-w-lg mx-auto mt-24 mx-4 sm:mx-auto bg-surface border border-border rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center gap-3 px-4 h-12 border-b border-border">
          <Search className="h-4 w-4 text-fg-muted" />
          <input
            type="text"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search docs…"
            className="flex-1 bg-transparent text-sm text-fg placeholder:text-fg-muted focus:outline-none"
          />
          <kbd className="text-2xs text-fg-subtle border border-border rounded px-1.5 py-0.5 font-mono">ESC</kbd>
        </div>
        <div className="max-h-80 overflow-y-auto p-2">
          {q.trim() === '' ? (
            <div className="px-3 py-6 text-center text-xs text-fg-muted">
              Type to search sections.
            </div>
          ) : results.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-fg-muted">
              No matches for "{q}".
            </div>
          ) : (
            results.map((r) => (
              <button
                key={r.slug}
                type="button"
                onClick={() => { onNavigate(r.slug); onClose(); }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-left hover:bg-surface-2/50"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-fg-muted uppercase tracking-wider">{r.eyebrow}</div>
                  <div className="text-sm font-medium text-fg truncate">{r.title}</div>
                </div>
                <ChevronRight className="h-3.5 w-3.5 text-fg-muted" />
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/* ===========================================================================
   INDIVIDUAL SECTION RENDERERS
   =========================================================================== */

function IntroductionSection() {
  return (
    <>
      <Lead>
        Scrape any page, solve any captcha, ship to production — through one API. Two endpoints, one predictable bill.
      </Lead>

      <Callout icon={Sparkles} color="info" title="Two endpoints, three engines">
        <code className="font-mono text-fg">POST /v1/fetch</code> scrapes any URL through the engine that fits it best — a fast TLS client for plain sites, a real browser for JS-heavy SPAs, and a premium path that solves Cloudflare for the protected ones. <code className="font-mono text-fg">POST /captcha</code> solves seven captcha types in one shot (reCAPTCHA v2/v3, hCaptcha, Turnstile, Geetest, image). You pick per call, or let Datames pick for you.
      </Callout>

      <Anchor id="what-can-datames-do">What can Datames do?</Anchor>
      <Lead>
        Scrape any public URL and get back clean, structured data — HTML, Markdown, plain text, or a PDF. Take screenshots. Solve Cloudflare challenges. Handle JavaScript-heavy SPAs. Pin a country, a proxy, or a session. Solve a captcha and POST the token to a target site. Drop the result straight into your agent, scraper, or data pipeline.
      </Lead>
      <Cards
        items={[
          { icon: Globe,       title: 'Scrape any page',          desc: 'GET, POST, PUT, DELETE, custom headers, cookies, sessions, request bodies.' },
          { icon: ShieldCheck, title: 'Cloudflare on demand',    desc: 'Challenge detected &rarr; solved &rarr; cookies cached for 30 minutes &rarr; follow-up calls drop back to the cheap path.' },
          { icon: Camera,      title: 'Screenshots &amp; PDFs',   desc: 'PNG or JPEG, full-page or element-scoped. PDF output via the browser engine. Drop straight into your product.' },
          { icon: Zap,         title: 'Captcha solving',          desc: 'reCAPTCHA v2 / v3, hCaptcha, Turnstile, Geetest, or generic image. POST your site + sitekey, get a token.' },
          { icon: Sparkles,    title: 'Clean output',             desc: 'HTML, Markdown, plain text, PDF, or auto-parsed JSON of the page contents.' },
          { icon: Hash,        title: 'Sessions &amp; cookies', desc: 'Pin a session_id to reuse cookies across calls for multi-step flows — login &rarr; scrape &rarr; submit.' },
        ]}
      />

      <Anchor id="why-datames">Why Datames?</Anchor>
      <ul className="text-sm text-fg-muted leading-relaxed space-y-2 list-disc pl-5 marker:text-fg-subtle">
        <li><strong className="text-fg">Predictable pricing.</strong> You see the exact credit cost in the response — base + extras = total. No surprise bills, no engineering meetings to figure out what you'll pay.</li>
        <li><strong className="text-fg">Built-in observability.</strong> Every call logs engine, latency, status, bytes, and challenge flag. Queryable from the dashboard or the API.</li>
        <li><strong className="text-fg">No lock-in.</strong> Plain HTTPS, plain JSON, plain auth header. cURL it, wrap it in your SDK, webhook the result.</li>
        <li><strong className="text-fg">Human support, not a queue.</strong> Send us the URL that broke, we add the right tweak and ship it within hours.</li>
      </ul>
    </>
  );
}

function QuickstartSection() {
  return (
    <>
      <Lead>
        Three steps from zero to your first scraped page.
      </Lead>

      <Anchor id="get-an-api-key">1. Get an API key</Anchor>
      <p className="text-sm text-fg-muted mb-3">
        Sign up, then open <strong className="text-fg">API Keys</strong> in the sidebar and click <strong className="text-fg">Create key</strong>.
        Save the plaintext — it's shown once.
      </p>

      <Anchor id="make-your-first-request">2. Make your first request</Anchor>
      <p className="text-sm text-fg-muted mb-3">
        POST a URL to <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded">/v1/fetch</code>:
      </p>
      <CodeBlock
        language="bash"
        filename="curl"
        code={`curl -X POST https://api.datames.dev/v1/fetch \\
  -H "x-api-key: sk_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://example.com"}'`}
      />
      <p className="text-sm text-fg-muted mb-3">
        The response includes the rendered HTML, the engine used, the status code, and the credit cost:
      </p>
      <CodeBlock
        language="json"
        filename="Response"
        code={`{
  "success": true,
  "engine": "tls_client_fast",
  "url": "https://example.com/",
  "status_code": 200,
  "challenge": false,
  "cost": { "base": 1, "base_label": "classic", "extras": 0, "total": 1 },
  "body": "<html>...</html>",
  "headers": { "...response headers..." }
}`}
      />

      <Anchor id="try-the-playground">3. Try the Playground</Anchor>
      <p className="text-sm text-fg-muted mb-3">
        The Playground in the dashboard lets you build a request visually, pick the engine, and copy a ready-made code snippet in 12 languages. No API key needed for unauthenticated previews — sign in to fire real requests.
      </p>
      <Callout icon={FlaskConical} color="info">
        Tip: open the Playground, switch to <code className="font-mono">stealth</code>, and target <code className="font-mono">https://nowsecure.nl</code> to see Datames solve a real Cloudflare challenge in &lt;2 seconds.
      </Callout>
    </>
  );
}

function EnginesSection() {
  return (
    <>
      <Lead>
        Three engines, one endpoint. Pick per call, or let the router decide.
      </Lead>

      <div className="my-5 space-y-5">
        <EngineCard
          name="Dynamic"
          tag="Default"
          tagline="Server picks the best engine for the target."
          details={[
            'Cheapest path that succeeds.',
            'Caches cf_clearance cookies for Cloudflare sites so future calls skip the solve.',
            'No surprises — same JSON, same endpoint.',
          ]}
        />
        <EngineCard
          name="Classic"
          tag="tls_client_fast / playwright_browser"
          tagline="Fast path or browser path, your choice. Operator's residential proxy slot."
          details={[
            'No Cloudflare escalation.',
            'Use when you need a specific country or a specific TLS fingerprint.',
            'Predictable 1 credit per call.',
          ]}
        />
        <EngineCard
          name="Stealth"
          tag="Datames"
          tagline="Datames solves Cloudflare and caches cf_clearance for 30 minutes."
          details={[
            'For sites that 403 plain HTTP clients.',
            'Always costs 4 credits.',
            'Set proxy_mode="stealth" to force it, even for non-CF sites.',
          ]}
        />
      </div>

      <Anchor id="choosing-an-engine">Choosing an engine</Anchor>
      <p className="text-sm text-fg-muted">
        Leave it on <strong className="text-fg">Dynamic</strong> unless you have a reason. The router does a one-shot probe (tls_client) and escalates to Datames only when the response is a high-confidence challenge. For sites that consistently challenge, set <code className="font-mono text-fg">proxy_mode="stealth"</code> to skip the probe and go straight to Datames.
      </p>
    </>
  );
}

function EngineCard({ name, tag, tagline, details }) {
  return (
    <div className="p-5 rounded-xl border border-border bg-surface">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="text-lg font-semibold text-fg">{name}</div>
          <div className="text-2xs uppercase tracking-wider text-fg-muted font-mono mt-0.5">{tag}</div>
        </div>
      </div>
      <div className="text-sm text-fg-muted mb-3">{tagline}</div>
      <ul className="text-xs text-fg-muted space-y-1.5 list-disc pl-5 marker:text-fg-subtle">
        {details.map((d, i) => <li key={i}>{d}</li>)}
      </ul>
    </div>
  );
}

function CloudflareSection() {
  return (
    <>
      <Lead>
        Cloudflare protection is detected automatically. Here's what happens behind the scenes.
      </Lead>

      <Anchor id="how-it-works">How it works</Anchor>
      <ol className="text-sm text-fg-muted leading-relaxed space-y-2 list-decimal pl-5 marker:text-fg-subtle">
        <li>You send a request to a Cloudflare-protected URL with no proxy_mode set.</li>
        <li>The router tries <code className="font-mono text-fg">tls_client_fast</code> first (cheap, 1 credit).</li>
        <li>If the response looks like a challenge (title "Just a moment…", 403 from a CF edge, etc.), the router escalates to Datames.</li>
        <li>Datames solves the challenge and returns the cleared page, plus a cached cf_clearance cookie.</li>
        <li>The next call to the same domain reuses the cookie — no solve needed for ~30 minutes.</li>
      </ol>

      <Anchor id="caching">Caching</Anchor>
      <p className="text-sm text-fg-muted">
        Cleared cookies are keyed by <code className="font-mono text-fg">session_id</code>. Pass one in your request to pin repeat calls to the same cf_clearance slot — useful for authenticated multi-step flows. Omit it to let the server generate a fresh slot per call.
      </p>

      <Anchor id="when-it-escalates">When it escalates</Anchor>
      <p className="text-sm text-fg-muted">
        The detector looks for high-confidence signals: the challenge title, challenge body markup, the CF orchestrate script URL, or a 403 from a Cloudflare edge. A site that just happens to be fronted by Cloudflare (with no challenge) won't trigger escalation — you'll stay on the cheap path.
      </p>
    </>
  );
}

function ChangelogSection() {
  return (
    <>
      <Lead>
        What's new in Datames. Newer entries at the top.
      </Lead>

      <Anchor id="latest-changes">Latest changes</Anchor>
      <ul className="text-sm text-fg-muted space-y-3 list-none pl-0">
        <ChangelogItem
          date="Recent"
          title="High-precision Cloudflare detector"
          body="Refined the challenge detector to stop escalating to Datames on every Cloudflare-fronted site. Real CF challenges still trigger escalation; sites that just use Cloudflare as a CDN now stay on the cheap path."
        />
        <ChangelogItem
          date="Recent"
          title="Configurable yearly discount"
          body="Yearly subscriptions are now 20% off by default. Operators can tune this in their config."
        />
        <ChangelogItem
          date="Recent"
          title="Plan changes without re-checkout"
          body="Upgrade, downgrade, or switch interval in one call. Downgrades are scheduled for the next renewal — no surprise charges."
        />
        <ChangelogItem
          date="Recent"
          title="Cookie-based sessions in /v1/fetch"
          body="Pass cookies alongside headers in the request body. Object-style (name/value/domain) is supported."
        />
      </ul>

      <Anchor id="in-progress">In progress</Anchor>
      <ul className="text-sm text-fg-muted space-y-2 list-disc pl-5 marker:text-fg-subtle">
        <li>Official Python SDK (today: cURL examples + plain HTTP).</li>
        <li>Per-domain allowed_status_codes overrides.</li>
        <li>Webhook delivery for completion notifications.</li>
        <li>Two-factor authentication for dashboard sign-in.</li>
      </ul>
    </>
  );
}

function ChangelogItem({ date, title, body }) {
  return (
    <li className="relative pl-6 border-l-2 border-border">
      <span className="absolute -left-2 top-1 h-3 w-3 rounded-full bg-surface border-2 border-brand" />
      <div className="text-2xs uppercase tracking-wider text-fg-muted">{date}</div>
      <div className="text-sm font-semibold text-fg mt-0.5">{title}</div>
      <div className="text-xs text-fg-muted mt-1 leading-relaxed">{body}</div>
    </li>
  );
}

function CaptchaSection() {
  return (
    <>
      <Lead>
        Solve any captcha in one shot. POST the site URL and sitekey, get back a token you can submit to the target. The solver runs in your browser context, not on a Datames-controlled page &mdash; that's the only way Google and Cloudflare allow the solve.
      </Lead>

      <EndpointHeader method="POST" path="/captcha" />

      <Anchor id="overview">Overview</Anchor>
      <p className="text-sm text-fg-muted mb-3">
        Every captcha type takes the same top-level fields plus a small set of type-specific ones. The
        <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded"> site_url</code> and
        <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded"> site_key</code> fields are required for the browser-based types (reCAPTCHA / hCaptcha / Turnstile).
        Geetest uses
        <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded"> site_url</code> plus a
        <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded"> gt</code> /
        <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded"> challenge</code> pair.
        <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded"> imageToText</code> takes a single base64
        <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded"> image</code> field.
      </p>

      <h3 className="text-sm font-semibold text-fg mt-6 mb-2">Top-level fields (shared by every type)</h3>
      <ParamTable
        rows={[
          { name: 'type',           type: 'string',         required: true,  desc: 'One of: recaptcha_v2_proxyless | recaptcha_v2_proxy | recaptcha_v3 | hcaptcha | turnstile | geetest | imageToText.' },
          { name: 'timeout_ms',     type: 'integer | null', required: false, desc: 'Per-solve timeout in milliseconds. Default: 60000 (60s). Max: 120000.' },
          { name: 'callback_url',   type: 'string | null',  required: false, desc: 'If set, Datames POSTs the result to this URL instead of returning it inline (useful for fire-and-forget solves that take longer than your HTTP timeout).' },
        ]}
      />

      <h3 className="text-sm font-semibold text-fg mt-6 mb-2">Per-type cost</h3>
      <p className="text-sm text-fg-muted mb-3">
        Charged once per solve attempt &mdash; whether the solve succeeds or not. See the
        <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded"> Credits</code> section for the full pricing model.
      </p>
      <table className="w-full text-xs my-4 rounded-lg border border-border overflow-hidden">
        <thead className="bg-surface-2/40">
          <tr className="text-2xs font-semibold uppercase tracking-wider text-fg-muted">
            <th className="text-left px-3 py-2">Type</th>
            <th className="text-left px-3 py-2">Cost</th>
            <th className="text-left px-3 py-2">What it solves</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">recaptcha_v2_proxyless</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">4 credits</td>
            <td className="px-3 py-2 text-fg-muted">Classic "I am not a robot" checkbox. Worker is a fresh browser.</td>
          </tr>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">recaptcha_v2_proxy</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">4 credits</td>
            <td className="px-3 py-2 text-fg-muted">Same checkbox, but routed through your supplied proxy.</td>
          </tr>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">recaptcha_v3</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">4 credits</td>
            <td className="px-3 py-2 text-fg-muted">Invisible score-based challenge. Returns a score between 0.0 and 1.0.</td>
          </tr>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">hcaptcha</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">5 credits</td>
            <td className="px-3 py-2 text-fg-muted">
              The Cloudflare-friendly hCaptcha checkbox.
              {' '}<strong>Steam URLs</strong>{' '}
              (<code className="text-2xs">store.steampowered.com/join/</code>){' '}
              cost <strong>4 credits</strong> &mdash; per-domain pipeline,
              cheaper because Steam's risk engine trusts our residential IP
              without a 9-grid.
              {' '}<strong>Discord URLs</strong>{' '}
              (<code className="text-2xs">discord.com/register</code>){' '}
              cost <strong>5 credits</strong> &mdash; per-domain pipeline
              fills the signup form (email, username, password, date of
              birth) to force hCaptcha to render, then auto-solves.
              Generic URLs: <strong>5 credits</strong>.
            </td>
          </tr>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">turnstile</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">4 credits</td>
            <td className="px-3 py-2 text-fg-muted">Cloudflare Turnstile. Often invisible; works the same way.</td>
          </tr>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">geetest</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">4 credits</td>
            <td className="px-3 py-2 text-fg-muted">Geetest slide / icon puzzle. Both v3 and v4 supported.</td>
          </tr>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">imageToText</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">1 credit</td>
            <td className="px-3 py-2 text-fg-muted">Generic image captcha &mdash; pass a base64 image, get the OCR answer back.</td>
          </tr>
        </tbody>
      </table>

      <Callout icon={AlertTriangle} color="warning" title="Failed attempts still cost">
        A 502 captcha_unsolved or a timeout charges the credits for the attempt. The solver always retries up to 3 rounds internally before declaring the attempt failed; you can observe this via the
        <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded"> rounds_attempted</code> field in the response.
      </Callout>

      <Anchor id="recaptcha_v2_proxyless">recaptcha_v2_proxyless</Anchor>
      <p className="text-sm text-fg-muted mb-3">
        Solves the standard "I am not a robot" checkbox. Worker is a fresh residential browser session &mdash; no proxy required from you.
      </p>
      <ParamTable
        rows={[
          { name: 'site_url',  type: 'string (URL)', required: true,  desc: 'The URL where the captcha is embedded. Required.' },
          { name: 'site_key',  type: 'string',       required: true,  desc: 'The 6Ld... public sitekey from the page. Required.' },
          { name: 'invisible', type: 'boolean',      required: false, desc: 'Set true if the target uses the V2 Invisible variant.' },
          { name: 'data_s',    type: 'string',       required: false, desc: 'For Enterprise V2 only. The recaptcha-data-s value from the page.' },
          { name: 'page_action', type: 'string',     required: false, desc: 'For Enterprise V2 only. The action label (e.g. "submit", "login").' },
        ]}
      />

      <Anchor id="recaptcha_v2_proxy">recaptcha_v2_proxy</Anchor>
      <p className="text-sm text-fg-muted mb-3">
        Same as the proxyless variant, but the solve is routed through your supplied proxy. Use this when the target checks the IP reputation of the submitter.
      </p>
      <ParamTable
        rows={[
          { name: 'site_url',       type: 'string (URL)',                required: true, desc: 'The URL where the captcha is embedded.' },
          { name: 'site_key',       type: 'string',                      required: true, desc: 'The 6Ld... public sitekey from the page.' },
          { name: 'proxy_type',     type: '"http" | "socks4" | "socks5"', required: true, desc: 'Which proxy protocol to use.' },
          { name: 'proxy_address',  type: 'string',                      required: true, desc: 'Proxy host or IP.' },
          { name: 'proxy_port',     type: 'integer',                     required: true, desc: 'Proxy port.' },
          { name: 'proxy_login',    type: 'string',                      required: false, desc: 'Proxy username, if auth is required.' },
          { name: 'proxy_password', type: 'string',                      required: false, desc: 'Proxy password, if auth is required.' },
          { name: 'invisible',      type: 'boolean',                     required: false, desc: 'Set true for the V2 Invisible variant.' },
        ]}
      />

      <Anchor id="recaptcha_v3">recaptcha_v3</Anchor>
      <p className="text-sm text-fg-muted mb-3">
        Invisible score-based challenge. Returns a token and a score (0.0&ndash;1.0); you submit the token to the target's <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded">g-recaptcha-response</code> form field.
      </p>
      <ParamTable
        rows={[
          { name: 'site_url',  type: 'string (URL)', required: true,  desc: 'The URL where the captcha is embedded.' },
          { name: 'site_key',  type: 'string',       required: true,  desc: 'The public sitekey.' },
          { name: 'action',    type: 'string',       required: false, desc: 'The action name to attach to the token (e.g. "login", "submit").' },
          { name: 'min_score', type: 'number',       required: false, desc: 'Minimum acceptable score (0.1&ndash;0.9). Tokens below this are rejected and the call returns 502 captcha_unsolved.' },
          { name: 'enterprise', type: 'boolean',     required: false, desc: 'Set true if the site uses reCAPTCHA Enterprise.' },
        ]}
      />

      <Anchor id="hcaptcha">hcaptcha</Anchor>
      <p className="text-sm text-fg-muted mb-3">
        Solves the hCaptcha checkbox (often used as a Cloudflare alternative). Same shape as the reCAPTCHA V2 fields.
      </p>
      <ParamTable
        rows={[
          { name: 'site_url', type: 'string (URL)', required: true, desc: 'The URL where the captcha is embedded.' },
          { name: 'site_key', type: 'string',       required: true, desc: 'The public sitekey (starts with a UUID, not 6Ld).' },
          { name: 'invisible', type: 'boolean',     required: false, desc: 'Set true if the target uses hCaptcha Invisible.' },
        ]}
      />

      <Anchor id="turnstile">turnstile</Anchor>
      <p className="text-sm text-fg-muted mb-3">
        Cloudflare Turnstile. Often invisible &mdash; works the same as the visible checkbox.
      </p>
      <ParamTable
        rows={[
          { name: 'site_url', type: 'string (URL)', required: true, desc: 'The URL where the captcha is embedded.' },
          { name: 'site_key', type: 'string',       required: true, desc: 'The Turnstile sitekey (starts with 0x...).' },
          { name: 'action',   type: 'string',       required: false, desc: 'Optional action name. Required by some Cloudflare-protected apps.' },
        ]}
      />

      <Anchor id="geetest">geetest</Anchor>
      <p className="text-sm text-fg-muted mb-3">
        Geetest slide / icon puzzle. Supports both v3 (the classic gt + challenge pair) and v4 (which auto-fetches the challenge from the captcha_id).
      </p>
      <ParamTable
        rows={[
          { name: 'site_url',    type: 'string (URL)',   required: true, desc: 'The URL where the captcha is embedded.' },
          { name: 'gt',          type: 'string',         required: false, desc: 'Geetest v3 "gt" identifier from the page. Required for v3.' },
          { name: 'challenge',   type: 'string',         required: false, desc: 'Geetest v3 "challenge" string from the page. Required for v3.' },
          { name: 'captcha_id',  type: 'string',         required: false, desc: 'Geetest v4 captcha_id. Required for v4.' },
          { name: 'version',     type: '"v3" | "v4"',    required: false, desc: 'Geetest version. Default: v3. Set to v4 when you only have captcha_id.' },
        ]}
      />

      <Anchor id="imageToText">imageToText</Anchor>
      <p className="text-sm text-fg-muted mb-3">
        Generic image captcha. Pass the image, get the OCR answer back in the
        <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded"> text</code> field.
      </p>
      <ParamTable
        rows={[
          { name: 'image', type: 'string (base64)', required: true, desc: 'Base64-encoded image. Accepts PNG / JPEG / WEBP, with or without a data URI prefix (data:image/png;base64,...).' },
        ]}
      />

      <Anchor id="response">Response</Anchor>
      <p className="text-sm text-fg-muted mb-3">
        Same shape regardless of type. Credits are deducted before the solve runs, so you'll see
        <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded"> credits_used</code> on every attempt whether the solve succeeded or not.
      </p>
      <CodeBlock
        language="json"
        code={`{
  "type": "turnstile",
  "token": "0.XYZ...abc123",
  "took_ms": 4230,
  "credits_used": 4,
  "rounds_attempted": 1,
  "recognition_calls": 1
}`}
      />
      <p className="text-sm text-fg-muted mt-3">
        For <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded">imageToText</code> the OCR answer comes back in the
        <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded"> text</code> field instead of
        <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded"> token</code>. For
        <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded"> geetest</code> v3 the response additionally includes
        <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded"> challenge</code> and
        <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded"> seccode</code>.
      </p>

      <Anchor id="errors">Errors</Anchor>
      <p className="text-sm text-fg-muted mb-3">
        The captcha endpoint uses the same HTTP status codes as <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded">/v1/fetch</code>. The captcha-specific <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded">error</code> codes in the body:
      </p>
      <ParamTable
        rows={[
          { name: 'invalid_captcha_request',  type: '400', desc: 'Type-specific validation failed before any solver work started (e.g. you forgot site_key, or the type doesn\'t support a field you passed).' },
          { name: 'rate_limited',             type: '429', desc: 'Per-key concurrent-solve limit. Wait and retry.' },
          { name: 'captcha_endpoint_not_implemented', type: '501', desc: 'Type is registered but its solver isn\'t wired up yet. Check the changelog.' },
          { name: 'recognition_error',        type: '502', desc: 'The recognition service returned an error we couldn\'t classify. Already-charged. Retry.' },
          { name: 'captcha_unsolved',         type: '502', desc: 'Recognition service ran but couldn\'t return a token in the allowed rounds. Already-charged. Retry.' },
          { name: 'captcha_solver_unavailable', type: '503', desc: 'Operator hasn\'t set up the recognition service for this endpoint yet.' },
          { name: 'solve_timeout',            type: '504', desc: 'The solve took longer than the configured timeout. Already-charged. Retry.' },
        ]}
      />

      <Anchor id="examples">Examples</Anchor>

      <h3 className="text-sm font-semibold text-fg mt-6 mb-2">reCAPTCHA v2 (no proxy)</h3>
      <CodeBlock
        language="bash"
        code={`curl -X POST https://api.datames.dev/captcha \\
  -H "x-api-key: ***" \\
  -H "Content-Type: application/json" \\
  -d '{
        "type": "recaptcha_v2_proxyless",
        "site_url": "https://example.com/login",
        "site_key": "6Lc...AbC"
      }'`}
      />

      <h3 className="text-sm font-semibold text-fg mt-6 mb-2">reCAPTCHA v2 with proxy</h3>
      <CodeBlock
        language="bash"
        code={`curl -X POST https://api.datames.dev/captcha \\
  -H "x-api-key: ***" \\
  -H "Content-Type: application/json" \\
  -d '{
        "type": "recaptcha_v2_proxy",
        "site_url": "https://example.com/login",
        "site_key": "6Lc...AbC",
        "proxy_type": "socks5",
        "proxy_address": "127.0.0.1",
        "proxy_port": 1080
      }'`}
      />

      <h3 className="text-sm font-semibold text-fg mt-6 mb-2">Cloudflare Turnstile</h3>
      <CodeBlock
        language="bash"
        code={`curl -X POST https://api.datames.dev/captcha \\
  -H "x-api-key: ***" \\
  -H "Content-Type: application/json" \\
  -d '{
        "type": "turnstile",
        "site_url": "https://protected.example.com",
        "site_key": "0x4AAA...xyz",
        "action": "submit"
      }'`}
      />

      <h3 className="text-sm font-semibold text-fg mt-6 mb-2">reCAPTCHA v3 with min_score</h3>
      <CodeBlock
        language="bash"
        code={`curl -X POST https://api.datames.dev/captcha \\
  -H "x-api-key: ***" \\
  -H "Content-Type: application/json" \\
  -d '{
        "type": "recaptcha_v3",
        "site_url": "https://example.com/checkout",
        "site_key": "6Lc...AbC",
        "action": "checkout",
        "min_score": 0.5
      }'`}
      />

      <h3 className="text-sm font-semibold text-fg mt-6 mb-2">Image captcha (OCR)</h3>
      <CodeBlock
        language="bash"
        code={`curl -X POST https://api.datames.dev/captcha \\
  -H "x-api-key: ***" \\
  -H "Content-Type: application/json" \\
  -d '{
        "type": "imageToText",
        "image": "data:image/png;base64,iVBORw0KGgo..."
      }'`}
      />
    </>
  );
}

function FetchSection() {
  return (
    <>
      <Lead>
        The single endpoint that does everything. POST a URL, get a rendered page back.
      </Lead>

      <EndpointHeader method="POST" path="/v1/fetch" />

      <Anchor id="endpoint">Endpoint</Anchor>
      <p className="text-sm text-fg-muted mb-3">
        <code className="font-mono text-fg bg-surface-2 px-1.5 py-0.5 rounded">POST https://api.datames.dev/v1/fetch</code>
      </p>

      <Anchor id="request-body">Request body</Anchor>
      <ParamTable
        rows={[
          { name: 'url', type: 'string (URL)', required: true, desc: 'The URL to fetch. http or https.' },
          { name: 'method', type: 'string', desc: 'HTTP method: GET, POST, PUT, DELETE, HEAD, PATCH, OPTIONS. Default: GET.' },
          { name: 'payload', type: 'object', desc: 'JSON body for non-GET requests.' },
          { name: 'headers', type: 'object', desc: 'Custom request headers as {"Name": "Value"} pairs.' },
          { name: 'cookies', type: 'array', desc: 'Cookies to send. Each: {"name": "...", "value": "...", "domain": "..."}.' },
          { name: 'proxy_mode', type: 'string | null', desc: '"classic" (cheap), "stealth" (Datames, forced), or null (dynamic, server picks). Default: null.' },
          { name: 'proxy_country', type: 'string', desc: '2-letter country code (e.g. "us", "de", "iq"). Optional.' },
          { name: 'session_id', type: 'string', desc: 'Pin repeat calls to the same cf_clearance cookie slot.' },
          { name: 'response_type', type: 'string', desc: '"html" | "text" | "markdown" | "pdf". Default: "html".' },
          { name: 'outputs', type: 'string[]', desc: 'Subset of fields to include in the response.' },
          { name: 'screenshot', type: 'boolean', desc: 'Include a base64 PNG/JPEG screenshot. +2 credits.' },
          { name: 'screenshot_fullpage', type: 'boolean', desc: 'Capture the entire scroll height. Requires screenshot=true.' },
          { name: 'screenshot_selector', type: 'string', desc: 'CSS selector to capture only a specific element. Requires screenshot=true.' },
          { name: 'screenshot_format', type: 'string', desc: '"png" | "jpeg". Default: "png". Requires screenshot=true.' },
          { name: 'screenshot_quality', type: 'integer', desc: 'JPEG quality 1-100. Only used when format="jpeg". Requires screenshot=true.' },
          { name: 'render_js', type: 'boolean', desc: 'Use the browser engine to execute JavaScript. Default: false.' },
          { name: 'js_instructions', type: 'string', desc: 'Multi-step instructions for the browser engine (clicks, scrolls, form fills).' },
          { name: 'wait_for', type: 'string', desc: 'CSS selector — wait until this element appears before returning.' },
          { name: 'wait', type: 'integer', desc: 'Extra milliseconds to wait after page load. 0-60000.' },
          { name: 'block_resources', type: 'boolean', desc: 'Skip images, fonts, CSS. Default: true.' },
          { name: 'permissive_status', type: 'boolean', desc: 'Return the body even on 4xx/5xx (success=false).' },
          { name: 'allowed_status_codes', type: 'integer[]', desc: 'HTTP codes considered "success". Default: [200, 201, 202, 203, 204, 206].' },
          { name: 'json_response', type: 'boolean', desc: 'Parse the response as JSON into result.data.' },
          { name: 'css_extractor', type: 'object', desc: 'Map of {"label": "css selector"} — extracted text per selector.' },
          { name: 'autoparse', type: 'boolean', desc: 'Try to find the main article / product info automatically.' },
          { name: 'device', type: 'string', desc: '"desktop" | "mobile" | "tablet". Default: "desktop".' },
          { name: 'viewport_width', type: 'integer', desc: 'Browser viewport width. 320-3840. Default: 1920.' },
          { name: 'viewport_height', type: 'integer', desc: 'Browser viewport height. 240-2160. Default: 1080.' },
          { name: 'timeout_ms', type: 'integer', desc: 'Per-request timeout in ms. 1000-120000.' },
          { name: 'tag', type: 'string', desc: 'Free-form label for grouping requests in billing reports. Max 64 chars.' },
        ]}
      />

      <Anchor id="response-shape">Response shape</Anchor>
      <ParamTable
        rows={[
          { name: 'success', type: 'boolean', desc: 'True if the call succeeded end-to-end.' },
          { name: 'engine', type: 'string', desc: 'Which engine served the call: tls_client_fast | playwright_browser | datames_stealth | datames_stealth_replay.' },
          { name: 'url', type: 'string', desc: 'The final URL after redirects.' },
          { name: 'status_code', type: 'integer', desc: 'HTTP status from the upstream target.' },
          { name: 'body', type: 'string | object', desc: 'The response body (HTML, text, markdown, parsed JSON, or PDF base64).' },
          { name: 'data', type: 'object', desc: 'Set when response_type="json" or json_response=true.' },
          { name: 'screenshot', type: 'string', desc: 'Base64-encoded screenshot when requested. data:image/png;base64,…' },
          { name: 'headers', type: 'object', desc: 'Subset of upstream response headers.' },
          { name: 'challenge', type: 'boolean', desc: 'True if Cloudflare was detected and bypassed.' },
          { name: 'cost', type: 'object', desc: '{base, base_label, extras, extras_total, total} — credits consumed.' },
        ]}
      />

      <Anchor id="examples">Examples</Anchor>
      <p className="text-sm text-fg-muted mb-3">Markdown output:</p>
      <CodeBlock
        language="bash"
        code={`curl -X POST https://api.datames.dev/v1/fetch \\
  -H "x-api-key: sk_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://example.com", "response_type": "markdown"}'`}
      />

      <p className="text-sm text-fg-muted mb-3 mt-5">Forced stealth + screenshot:</p>
      <CodeBlock
        language="bash"
        code={`curl -X POST https://api.datames.dev/v1/fetch \\
  -H "x-api-key: sk_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://nowsecure.nl", "proxy_mode": "stealth", "screenshot": true}'`}
      />
    </>
  );
}

function KeysSection() {
  return (
    <>
      <Lead>
        API keys are how you authenticate. One key per environment (dev / staging / prod) keeps things tidy.
      </Lead>

      <Anchor id="list-keys">List your keys</Anchor>
      <EndpointHeader method="GET" path="/v1/keys" />
      <p className="text-sm text-fg-muted mb-3">
        Returns all keys for the authenticated customer, with metadata only (no plaintext).
      </p>

      <Anchor id="create-a-key">Create a key</Anchor>
      <EndpointHeader method="POST" path="/v1/keys" />
      <CodeBlock
        language="bash"
        code={`curl -X POST https://api.datames.dev/v1/keys?label=production \\
  -H "x-api-key: sk_YOUR_EXISTING_KEY"`}
      />
      <p className="text-sm text-fg-muted mb-3">
        The plaintext key is returned once in the response. Save it — we don't store it.
      </p>

      <Anchor id="use-a-key">Use a key</Anchor>
      <p className="text-sm text-fg-muted mb-3">
        Pass it as the <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded">x-api-key</code> header on every request:
      </p>
      <CodeBlock
        language="bash"
        code={`curl https://api.datames.dev/v1/billing \\
  -H "x-api-key: sk_YOUR_KEY"`}
      />

      <Anchor id="revoke-a-key">Revoke a key</Anchor>
      <EndpointHeader method="DELETE" path="/v1/keys/{id}" />
      <p className="text-sm text-fg-muted">
        Revocation is idempotent. Once revoked, the key is dead — subsequent calls return <code className="font-mono text-fg">401 Unauthorized</code>.
      </p>
    </>
  );
}

function BillingSection() {
  return (
    <>
      <Lead>
        Plans, subscriptions, and top-ups. Pay with crypto (NOWPayments) or with a secure credit or debit card through our processor &mdash; no card numbers touch our servers.
      </Lead>

      <Anchor id="plans">Plans</Anchor>
      <EndpointHeader method="GET" path="/payments/plans" />
      <p className="text-sm text-fg-muted mb-3">Returns all public plans with their monthly USD price, monthly credit refill, and per-minute rate limit:</p>
      <CodeBlock
        language="json"
        code={`{
  "plans": [
    { "code": "free",       "display_name": "Free",          "monthly_price_usd": 0,     "initial_credits": 50,      "rate_limit_per_minute": 1,  "over_usage_usd_per_1k": null },
    { "code": "starter",    "display_name": "Starter Plan",  "monthly_price_usd": 4.99,  "initial_credits": 10000,   "rate_limit_per_minute": 2,  "over_usage_usd_per_1k": 1.0 },
    { "code": "standard",   "display_name": "Standard Plan", "monthly_price_usd": 19.99, "initial_credits": 50000,   "rate_limit_per_minute": 5,  "over_usage_usd_per_1k": 0.8 },
    { "code": "pro",        "display_name": "Pro Plan",      "monthly_price_usd": 49.99, "initial_credits": 165000,  "rate_limit_per_minute": 10, "over_usage_usd_per_1k": 0.65 },
    { "code": "enterprise", "display_name": "Enterprise Plan","monthly_price_usd": 99.99,"initial_credits": 400000,  "rate_limit_per_minute": 50, "over_usage_usd_per_1k": 0.5 }
  ]
}`}
      />
      <p className="text-sm text-fg-muted mt-3">
        Most plans also support <strong className="text-fg">quarterly</strong> billing at a discount &mdash; roughly 3&times; the monthly credits for ~2.4&times; the monthly price. Use the
        <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded"> quarterly_price_usd</code> and
        <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded"> quarterly_initial_credits</code> fields for that.
      </p>

      <Anchor id="billing-summary">Your billing summary</Anchor>
      <EndpointHeader method="GET" path="/v1/billing" />
      <p className="text-sm text-fg-muted mb-3">What the dashboard shows:</p>
      <CodeBlock
        language="json"
        code={`{
  "plan_code": "pro",
  "credit_balance": 42150,
  "total_purchased": 165000,
  "total_consumed": 122850,
  "active_subscription_end": "2026-08-05T12:34:56+00:00"
}`}
      />

      <Anchor id="subscription">Start a subscription</Anchor>
      <EndpointHeader method="POST" path="/payments/checkout/subscription" />
      <p className="text-sm text-fg-muted mb-3">Pass the plan + billing interval. Returns a checkout URL the customer opens in their browser to pay.</p>
      <CodeBlock
        language="bash"
        code={`curl -X POST https://api.datames.dev/payments/checkout/subscription \\
  -H "x-api-key: ***" \\
  -H "Content-Type: application/json" \\
  -d '{"plan_code": "pro", "billing_interval": "monthly"}'`}
      />

      <Anchor id="top-ups">Top up credits</Anchor>
      <EndpointHeader method="POST" path="/payments/checkout/topup" />
      <p className="text-sm text-fg-muted mb-3">Buy credits without changing your plan. Top-ups are only available when your balance is zero (the <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded">/payments/topup-eligibility</code> endpoint tells you why if not).</p>
      <CodeBlock
        language="bash"
        code={`curl -X POST https://api.datames.dev/payments/checkout/topup \\
  -H "x-api-key: ***" \\
  -H "Content-Type: application/json" \\
  -d '{"usd_cents": 1000}'`}
      />

      <Anchor id="renewal-status">Renewal status (for the dashboard banner)</Anchor>
      <EndpointHeader method="GET" path="/payments/renewal-status" />
      <p className="text-sm text-fg-muted mb-3">Powers the "your plan renews in N days" banner. Single source of truth for renewal timing, grace window, and what action (if any) the customer should take.</p>
      <CodeBlock
        language="json"
        code={`{
  "plan_code": "pro",
  "billing_interval": "monthly",
  "next_renewal": "2026-08-05T12:34:56+00:00",
  "days_until_renewal": 23,
  "frozen": false,
  "expired": false,
  "action_required": null,
  "amount_due_cents": 4999,
  "pending_re_subscription": null,
  "active_balance": 42150
}`}
      />

      <Anchor id="payment-history">Payment history</Anchor>
      <EndpointHeader method="GET" path="/payments/history" />
      <p className="text-sm text-fg-muted">
        Lists this customer's payment history (newest first). Each row has id, kind (subscription / topup), plan_code, amount_cents, credits_granted, status, pay_currency, pay_address, created_at / paid_at / expires_at.
      </p>
    </>
  );
}

function AnalyticsSection() {
  return (
    <>
      <Lead>
        Programmatic access to your usage — power dashboards, internal reports, or charge-back logic.
      </Lead>

      <Anchor id="summary">Summary</Anchor>
      <EndpointHeader method="GET" path="/v1/analytics/summary" />
      <p className="text-sm text-fg-muted mb-3">
        Totals for the current billing period: requests, credits spent, success rate, engine breakdown, top domains.
      </p>

      <Anchor id="logs">Logs</Anchor>
      <EndpointHeader method="GET" path="/v1/logs" />
      <CodeBlock
        language="bash"
        code={`curl 'https://api.datames.dev/v1/logs?limit=50' \\
  -H "x-api-key: sk_YOUR_KEY"`}
      />
      <p className="text-sm text-fg-muted">
        One row per call. Includes URL, method, engine, status, latency, cost, timestamp. Default 50, max 200 per page.
      </p>

      <Anchor id="daily-usage">Daily usage</Anchor>
      <EndpointHeader method="GET" path="/v1/logs/timeseries" />
      <CodeBlock
        language="bash"
        code={`curl 'https://api.datames.dev/v1/logs/timeseries?days=30' \\
  -H "x-api-key: sk_YOUR_KEY"`}
      />
      <p className="text-sm text-fg-muted">
        Per-day counts and credit totals. Same shape as the dashboard charts.
      </p>

      <Anchor id="per-engine-cost">Per-engine cost</Anchor>
      <p className="text-sm text-fg-muted">
        Every row has an <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded">engine</code> field. Group your queries by it to see how much you're spending on tls_client vs playwright vs Datames.
      </p>
    </>
  );
}

function ErrorsSection() {
  return (
    <>
      <Lead>
        HTTP status codes and what they mean. We aim for predictable, debuggable responses.
      </Lead>

      <Anchor id="http-status-codes">HTTP status codes</Anchor>
      <ParamTable
        rows={[
          { name: '200',  type: 'OK',                    desc: 'Success. The body field contains the rendered page (HTML / markdown / text / PDF / parsed JSON).' },
          { name: '400',  type: 'Bad Request',           desc: 'Malformed JSON, invalid required field, or an explicit rejection (e.g. response_type=pdf + proxy_mode=stealth).' },
          { name: '401',  type: 'Unauthorized',          desc: 'Missing x-api-key header or the key is invalid / revoked.' },
          { name: '402',  type: 'Payment Required',      desc: 'No credits left. Top up or upgrade your plan.' },
          { name: '404',  type: 'Not Found',             desc: 'The plan code, key id, captcha type, or path you requested does not exist.' },
          { name: '422',  type: 'Unprocessable Entity',  desc: 'Pydantic validation failed (e.g. screenshot_fullpage=true without screenshot=true, screenshot_quality out of range, response_type not in {html, text, markdown, pdf}).' },
          { name: '429',  type: 'Too Many Requests',     desc: 'You hit your per-key concurrent-request limit. Wait a moment and retry.' },
          { name: '500',  type: 'Internal Server Error', desc: 'Something on our end broke. Retry with exponential backoff. The traceback is logged server-side, but the response is opaque (just an error message + request_id).' },
          { name: '502',  type: 'Bad Gateway',           desc: 'The target returned a non-2xx (and permissive_status was off), or our backend could not complete the request. For /captcha: also returned as the captcha_unsolved envelope.' },
          { name: '503',  type: 'Service Unavailable',   desc: 'Server busy, full queue, or a downstream dependency is down. For /captcha: captcha_solver_unavailable (no recognition API key configured). The Retry-After header tells you how long.' },
          { name: '504',  type: 'Gateway Timeout',       desc: 'The scrape took longer than the configured timeout (default 90s, max 120s). For /captcha: solve_timeout.' },
        ]}
      />

      <Anchor id="common-error-responses">Common error responses</Anchor>
      <p className="text-sm text-fg-muted mb-3">
        4xx responses include a JSON body explaining what went wrong:
      </p>
      <CodeBlock
        language="json"
        filename="Example 422"
        code={`{
  "detail": {
    "error": "screenshot_fullpage requires screenshot=true"
  }
}`}
      />
      <CodeBlock
        language="json"
        filename="Example 402"
        code={`{
  "detail": {
    "error": "no_credits",
    "credit_balance": 0,
    "monthly_credit_refill": 165000
  }
}`}
      />

      <Anchor id="retries">Retries</Anchor>
      <Callout icon={AlertTriangle} color="warning" title="Retry the right things">
        Retry only on <code className="font-mono">5xx</code> and <code className="font-mono">429</code>. Respect the <code className="font-mono">Retry-After</code> header. Exponential backoff (1s &rarr; 2s &rarr; 4s) is the polite default. A 402 (no credits) won't fix itself &mdash; that's a billing problem, not a transient one.
      </Callout>
    </>
  );
}

function PlansSection() {
  return (
    <>
      <Lead>
        Four plans plus a free tier. Pick what matches the volume you need.
      </Lead>

      <div className="my-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <PlanCard name="Free" price="0" refill="50" rate="1/min" desc="50 credits one-time on signup. Cheap path + browser path only — premium engine not included. No top-ups." />
        <PlanCard name="Starter" price="$4.99 / mo" refill="10,000" rate="2/min" desc="10k credits monthly, all engines. Top-ups at $1 / 1k credits." />
        <PlanCard name="Standard" price="$19.99 / mo" refill="50,000" rate="5/min" desc="50k credits monthly. Top-ups at $0.80 / 1k credits. Recommended for agents and small pipelines." highlight />
        <PlanCard name="Pro" price="$49.99 / mo" refill="165,000" rate="10/min" desc="165k credits monthly. Top-ups at $0.65 / 1k credits. For production scrapers." />
        <PlanCard name="Enterprise" price="$99.99 / mo" refill="400,000" rate="50/min" desc="400k credits monthly. Top-ups at $0.50 / 1k credits. Dedicated support." />
      </div>

      <Anchor id="free">Free</Anchor>
      <ul className="text-sm text-fg-muted leading-relaxed space-y-2 list-disc pl-5 marker:text-fg-subtle">
        <li><strong className="text-fg">50 credits</strong> &mdash; one-time grant on signup, not monthly. (2026-07-12: reduced from 100.)</li>
        <li><strong className="text-fg">1 request / minute</strong></li>
        <li>Top-ups: not available</li>
        <li>Best for: trying the API, building a small proof-of-concept.</li>
      </ul>

      <Anchor id="starter">Starter</Anchor>
      <ul className="text-sm text-fg-muted leading-relaxed space-y-2 list-disc pl-5 marker:text-fg-subtle">
        <li><strong className="text-fg">$4.99 / month</strong> &mdash; or $11.99 quarterly (30k credits)</li>
        <li><strong className="text-fg">10,000 credits / month</strong></li>
        <li><strong className="text-fg">2 requests / minute</strong></li>
        <li>Top-ups at $1.00 / 1k credits (when balance is zero)</li>
        <li>Best for: side projects, low-volume agents.</li>
      </ul>

      <Anchor id="standard">Standard</Anchor>
      <ul className="text-sm text-fg-muted leading-relaxed space-y-2 list-disc pl-5 marker:text-fg-subtle">
        <li><strong className="text-fg">$19.99 / month</strong> &mdash; or $47.99 quarterly (150k credits)</li>
        <li><strong className="text-fg">50,000 credits / month</strong></li>
        <li><strong className="text-fg">5 requests / minute</strong></li>
        <li>Top-ups at $0.80 / 1k credits (when balance is zero)</li>
        <li>Best for: serious scrapers, small AI agents, recurring data pipelines.</li>
      </ul>

      <Anchor id="pro">Pro</Anchor>
      <ul className="text-sm text-fg-muted leading-relaxed space-y-2 list-disc pl-5 marker:text-fg-subtle">
        <li><strong className="text-fg">$49.99 / month</strong> &mdash; or $119.99 quarterly (495k credits)</li>
        <li><strong className="text-fg">165,000 credits / month</strong></li>
        <li><strong className="text-fg">10 requests / minute</strong></li>
        <li>Top-ups at $0.65 / 1k credits (when balance is zero)</li>
        <li>Best for: production scrapers, multi-tenant agents.</li>
      </ul>

      <Anchor id="enterprise">Enterprise</Anchor>
      <ul className="text-sm text-fg-muted leading-relaxed space-y-2 list-disc pl-5 marker:text-fg-subtle">
        <li><strong className="text-fg">$99.99 / month</strong> &mdash; or $239.99 quarterly</li>
        <li><strong className="text-fg">400,000 credits / month</strong></li>
        <li><strong className="text-fg">50 requests / minute</strong></li>
        <li>Top-ups at $0.50 / 1k credits (when balance is zero)</li>
        <li>Best for: production pipelines, multi-tenant agents, heavy use.</li>
      </ul>

      <Anchor id="choosing-a-plan">Which one should I pick?</Anchor>
      <p className="text-sm text-fg-muted">
        Start on Free to try the API. When you start hitting walls (the 1/min rate limit, or running out of credits before the month ends), upgrade. Each step up roughly doubles your monthly credits and increases the rate limit. You can change plans any time from the dashboard &mdash; upgrades are immediate, downgrades take effect at the next renewal.
      </p>
    </>
  );
}

function PlanCard({ name, price, refill, rate, desc, highlight }) {
  return (
    <div className={`p-4 rounded-xl border ${highlight ? 'border-brand bg-brand-softest' : 'border-border bg-surface'}`}>
      <div className="flex items-center justify-between">
        <div className="text-lg font-semibold text-fg">{name}</div>
        {highlight && <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-brand text-brand-fg">Popular</span>}
      </div>
      <div className="text-2xl font-bold text-fg mt-1">{price}</div>
      <ul className="mt-2 text-xs text-fg-muted space-y-1">
        <li className="flex items-center gap-1.5"><Hash className="h-3 w-3" /> {refill} credits / month</li>
        <li className="flex items-center gap-1.5"><Activity className="h-3 w-3" /> {rate}</li>
      </ul>
      <div className="text-xs text-fg-muted mt-2 leading-relaxed">{desc}</div>
    </div>
  );
}

function CreditsSection() {
  return (
    <>
      <Lead>
        One credit unit, charged per call. The cost depends on which engine runs and what you ask it to do. We always tell you the exact cost in the response &mdash; no surprises.
      </Lead>

      <Anchor id="how-credits-work">How credits work</Anchor>
      <ul className="text-sm text-fg-muted leading-relaxed space-y-2 list-disc pl-5 marker:text-fg-subtle">
        <li>Every call deducts credits. Whether the call succeeds or fails &mdash; you still pay (anti-abuse: prevents intentionally-broken URLs from draining the budget).</li>
        <li>Plans refill monthly on the anniversary of your subscription start date.</li>
        <li>Top-up credits never expire and stack on top of your plan's monthly refill.</li>
        <li>The response always includes a <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded">cost</code> object: <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded">base + extras = total</code>. Read this if you want to know why.</li>
      </ul>

      <Anchor id="fetch-costs">Fetch costs (POST /v1/fetch)</Anchor>
      <h3 className="text-sm font-semibold text-fg mt-6 mb-2">Base cost by engine</h3>
      <table className="w-full text-xs my-4 rounded-lg border border-border overflow-hidden">
        <thead className="bg-surface-2/40">
          <tr className="text-2xs font-semibold uppercase tracking-wider text-fg-muted">
            <th className="text-left px-3 py-2">Engine</th>
            <th className="text-left px-3 py-2">Base</th>
            <th className="text-left px-3 py-2">When it runs</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">tls_client_fast</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">1 credit</td>
            <td className="px-3 py-2 text-fg-muted">Static pages, API responses, sites with cached cf_clearance cookies</td>
          </tr>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">playwright_browser</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">2 credits</td>
            <td className="px-3 py-2 text-fg-muted">Anything that needs a real browser &mdash; JS rendering, screenshots, PDFs, multi-step interactions</td>
          </tr>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">datames_stealth</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">5 credits</td>
            <td className="px-3 py-2 text-fg-muted">Cloudflare-protected sites where the challenge was actually solved (not replayed)</td>
          </tr>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">datames_stealth_replay</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">1 credit</td>
            <td className="px-3 py-2 text-fg-muted">Follow-up call in the same 30-min cf_clearance cache window</td>
          </tr>
        </tbody>
      </table>

      <h3 className="text-sm font-semibold text-fg mt-6 mb-2">Extras (additive on top of the base)</h3>
      <table className="w-full text-xs my-4 rounded-lg border border-border overflow-hidden">
        <thead className="bg-surface-2/40">
          <tr className="text-2xs font-semibold uppercase tracking-wider text-fg-muted">
            <th className="text-left px-3 py-2">Extra</th>
            <th className="text-left px-3 py-2">Cost</th>
            <th className="text-left px-3 py-2">Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">js_render</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">+1 credit</td>
            <td className="px-3 py-2 text-fg-muted">Triggers the browser engine; pairs with the +1 needed for the base</td>
          </tr>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">js_instructions</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">+1 credit</td>
            <td className="px-3 py-2 text-fg-muted">Same engine cost as js_render (won't stack if both are set)</td>
          </tr>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">screenshot</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">+1 credit</td>
            <td className="px-3 py-2 text-fg-muted">Plus the browser-engine cost on top</td>
          </tr>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">response_type=pdf</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">+1 credit</td>
            <td className="px-3 py-2 text-fg-muted">Only works on classic or playwright. Stealth + pdf is rejected with 400.</td>
          </tr>
        </tbody>
      </table>

      <Anchor id="captcha-costs">Captcha costs (POST /captcha)</Anchor>
      <p className="text-sm text-fg-muted mb-3">
        Different type, different price. Charged once per solve attempt &mdash; whether the solve succeeds or not.
      </p>
      <table className="w-full text-xs my-4 rounded-lg border border-border overflow-hidden">
        <thead className="bg-surface-2/40">
          <tr className="text-2xs font-semibold uppercase tracking-wider text-fg-muted">
            <th className="text-left px-3 py-2">Type</th>
            <th className="text-left px-3 py-2">Cost</th>
            <th className="text-left px-3 py-2">Where you'll see it</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">recaptcha_v2_proxyless</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">4 credits</td>
            <td className="px-3 py-2 text-fg-muted">Classic "I am not a robot" checkbox</td>
          </tr>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">recaptcha_v2_proxy</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">4 credits</td>
            <td className="px-3 py-2 text-fg-muted">Same checkbox, with proxy-aware solve</td>
          </tr>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">recaptcha_v3</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">4 credits</td>
            <td className="px-3 py-2 text-fg-muted">Invisible score-based challenge</td>
          </tr>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">hcaptcha</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">5 credits</td>
            <td className="px-3 py-2 text-fg-muted">
              The Cloudflare-friendly hCaptcha checkbox.
              {' '}<strong>Steam URLs</strong>{' '}
              (<code className="text-2xs">store.steampowered.com/join/</code>){' '}
              cost <strong>4 credits</strong> &mdash; per-domain pipeline,
              cheaper because Steam's risk engine trusts our residential IP
              without a 9-grid.
              {' '}<strong>Discord URLs</strong>{' '}
              (<code className="text-2xs">discord.com/register</code>){' '}
              cost <strong>5 credits</strong> &mdash; per-domain pipeline
              fills the signup form (email, username, password, date of
              birth) to force hCaptcha to render, then auto-solves.
              Generic URLs: <strong>5 credits</strong>.
            </td>
          </tr>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">turnstile</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">4 credits</td>
            <td className="px-3 py-2 text-fg-muted">Cloudflare Turnstile (often invisible)</td>
          </tr>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">geetest</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">4 credits</td>
            <td className="px-3 py-2 text-fg-muted">Geetest slide / icon puzzle</td>
          </tr>
          <tr className="border-t border-border align-top">
            <td className="px-3 py-2 font-mono">imageToText</td>
            <td className="px-3 py-2 font-mono whitespace-nowrap">1 credit</td>
            <td className="px-3 py-2 text-fg-muted">Generic image captcha &mdash; pass a base64 image, get the answer back</td>
          </tr>
        </tbody>
      </table>

      <Anchor id="top-ups">Top-ups</Anchor>
      <p className="text-sm text-fg-muted">
        Run out mid-job? Buy a top-up from the dashboard. Top-ups are only available when your balance is zero (the <code className="font-mono text-fg bg-surface-2 px-1 py-0.5 rounded">/payments/topup-eligibility</code> endpoint tells you why if not). The cost is capped at your plan's monthly refill amount and the rate is the same as your plan's over-usage rate. Top-up credits never expire.
      </p>

      <Anchor id="refunds">Refunds</Anchor>
      <p className="text-sm text-fg-muted">
        Failed requests that never returned a usable response are credited back automatically — this is in <code className="font-mono text-fg">result.cost</code>. Crypto payments themselves are non-refundable once confirmed on-chain; reach out to <a href="mailto:support@datames.com" className="text-brand hover:underline">support</a> for edge cases.
      </p>
    </>
  );
}